import { FC, useCallback, useMemo } from "react";
import moment from "moment/moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnceWhen } from "rooks";
import { DateRangePicker } from "../../../common/date-range-picker/DateRangePicker";
import {
  getRreDefinedAllRangesForExecutionLogs,
  MAX_DATE_MONTHS,
} from "../utils/constants/dates";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setExecutionLogDates,
  setExecutionLogPagination,
} from "@/store/execution-log/executionLogSlice";
import { getExecutionLogThunk } from "@/store/execution-log/thunks/getExecutionLogThunk";
import {
  Dates,
  useGetUsersMeExecutionLogsMinimumAvailableDateQuery,
} from "@/services/cloudchipr.api";
import { executionLogDatesSelector } from "@/store/execution-log/selectors/executionLogDatesSelector";
import { executionLogDateFilterKey } from "@/components/pages/execution-log/utils/constants/common";

export const ExecutionLogsDateRangeSelector: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const dates = useAppSelector(executionLogDatesSelector);
  const { data } = useGetUsersMeExecutionLogsMinimumAvailableDateQuery();
  const minAvailableDate = data?.minimum_available_date ?? undefined;
  const ranges = useMemo(
    () => getRreDefinedAllRangesForExecutionLogs(minAvailableDate),
    [minAvailableDate],
  );

  const [searchParams] = useSearchParams();
  const urlSourceDate = searchParams.get(executionLogDateFilterKey);

  const submitHandler = useCallback(
    (dates: Dates) => {
      dispatch(setExecutionLogDates(dates));
      dispatch(setExecutionLogPagination({ page: 0 }));
      dispatch(getExecutionLogThunk({}));
    },
    [dispatch],
  );

  useEffectOnceWhen(() => {
    if (!urlSourceDate) {
      return;
    }

    setTimeout(() => {
      dispatch(
        setExecutionLogDates({
          from: urlSourceDate,
          to: urlSourceDate,
        }),
      );
      dispatch(setExecutionLogPagination({ page: 0 }));
      dispatch(getExecutionLogThunk({}));

      searchParams.delete(executionLogDateFilterKey);

      navigate({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }, 0);
  }, !!urlSourceDate);

  return (
    <DateRangePicker
      highlightToday
      minDate={minAvailableDate ?? minDate}
      selectedDates={dates as Dates}
      onDateSelect={submitHandler}
      availableMinDate={minAvailableDate}
      ranges={ranges}
      size="xsmall"
    />
  );
};

const minDate = moment()
  .subtract(MAX_DATE_MONTHS, "month")
  .startOf("month")
  .format();
